import { Stack, Text } from '@mantine/core';

import type { RegistryDigestDocumentSection } from '@/types/registry-documents';
import {
  DOCUMENT_WORDS,
  documentEntrySummary,
  documentIssuerLine,
  documentNoteLines,
  documentSectionTitle,
  keyFactRows,
} from './digestDocuments';
import { CorporateInformation, FinancialInformation } from './DigestSections';
import { LabelValueTable } from './DigestTables';
import { coverageSentence, sourceLine } from './digestTemplate';

interface DigestDocumentEntryProps {
  section: RegistryDigestDocumentSection;
  /** The registry the order's sources name: what the lead-ins say when
   * the document's head names none. */
  registry: string;
  /** The order's company name: what the lead-ins fall back to. */
  companyName: string;
  /** When the documents were delivered, formatted, for a source that
   * gives no retrieved date. */
  deliveredAt: string | null;
}

/** Whether the entry's role gives it a body: the corporate tables, the
 * financial figures, or at least one key fact. */
function hasBody(section: RegistryDigestDocumentSection): boolean {
  if (section.role === 'corporate' && section.corporate) {
    return true;
  }
  if (section.role === 'financial' && section.financials) {
    return true;
  }
  return keyFactRows(section).length > 0;
}

/** The body the document's role gives it: the registration, management
 * and ownership tables for a register extract, the periods table with
 * the auditor lines for accounts, the key-facts table otherwise. */
function EntryBody({
  section,
  registry,
  companyName,
}: Readonly<Pick<DigestDocumentEntryProps, 'section' | 'registry' | 'companyName'>>) {
  if (section.role === 'corporate' && section.corporate) {
    return (
      <CorporateInformation
        corporate={section.corporate}
        registry={registry}
        companyName={companyName}
      />
    );
  }
  if (section.role === 'financial' && section.financials) {
    return <FinancialInformation financials={section.financials} registry={registry} />;
  }
  const facts = keyFactRows(section);
  if (facts.length === 0) {
    return null;
  }
  return <LabelValueTable rows={facts} label={`${documentSectionTitle(section)}: key facts`} />;
}

/**
 * One document's section under its heading, as the report lays it out:
 * the issuer line, the head's summary (what the document is and what it
 * tells the reader), the body its role gives, this file's sources on one
 * line, a "Note:" line per caveat, and the sentence saying what was not
 * read of a cut document. Data in and markup out.
 */
export function DigestDocumentEntry({
  section,
  registry,
  companyName,
  deliveredAt,
}: Readonly<DigestDocumentEntryProps>) {
  const entryRegistry = section.head?.registry?.trim() || registry;
  const issuer = documentIssuerLine(section);
  const summary = documentEntrySummary(section);
  const sources = section.sources.map((source) => sourceLine(source, deliveredAt));
  const notes = documentNoteLines(section);
  const cut = section.coverage ? coverageSentence(section.coverage) : null;
  return (
    <Stack gap="xs" data-testid="registry-digest-entry">
      {issuer && <Text size="sm">{issuer}</Text>}
      {summary && <Text size="sm">{summary}</Text>}
      {hasBody(section) ? (
        <EntryBody section={section} registry={entryRegistry} companyName={companyName} />
      ) : (
        !summary && <Text size="sm">{DOCUMENT_WORDS.noInformation}</Text>
      )}
      {sources.length > 0 && (
        <Text size="xs" c="dimmed" data-testid="registry-digest-entry-sources">
          {sources.length === 1 ? 'Source' : 'Sources'}: {sources.join('; ')}
        </Text>
      )}
      {notes.map((note) => (
        <Text key={note} size="xs" data-testid="registry-digest-entry-note">
          {note}
        </Text>
      ))}
      {cut && (
        <Text size="sm" c="orange" data-testid="registry-digest-coverage">
          {cut}
        </Text>
      )}
    </Stack>
  );
}
